import type { FetchArgType } from "openapi-typescript-fetch";
import type {
    GetListParams,
    GetListResult,
    GetManyReferenceParams,
    GetManyReferenceResult,
    UpdateParams,
    UpdateManyParams,
    CreateParams,
    DeleteManyParams,
    DeleteManyResult,
    UpdateResult,
    GetManyResult,
    CreateResult
} from "react-admin";
import { isEmpty } from "lodash";

import { fetcher } from "../../data";
import { isDefined } from "../../utils/typedLoDashUtils";
import { assertIsDefined } from "../../utils/assertions";
import type { BaseFilters } from "../../data/BaseFilters";
import { NotImplementedError } from "../../utils/NotImplementedError";
import type { ErrorDetails, LangParams, MpmDataProvider, MpmDataProviderOperationName } from "../../data";

import type { SelectionDTO } from "./SelectionDTO";
import type { SelectionRecord, SelectionTargetType } from "./SelectionRecord";
import { convertIdFilter } from "../../utils/filterUtils";
import { ContentStatus } from "../../model/content-status";
import { PublicationStatus } from "../../model/publication-status";

const findAll = fetcher.path("/api/edito/selection/findAll").method("post").create();
const getOne = fetcher.path("/api/edito/selection/{uuid}").method("get").create();
const updateOne = fetcher.path("/api/edito/selection/{uuid}").method("put").create();
const createOne = fetcher.path("/api/edito/selection/").method("post").create();
const deleteOne = fetcher.path("/api/edito/selection/{uuid}").method("delete").create();
const getAllByUuids = fetcher.path("/api/edito/selection/allByUuids").method("get").create();

/**
 * Parses the DTO from backend to record for frontend
 * @param dto
 * @returns SelectionRecord
 */

function mapDTOToRecord(dto: SelectionDTO): SelectionRecord {
    // DTO from backend
    const {
        id = 0,
        uuid = "",
        title = "",
        description = "",
        createdAt = null,
        status,
        minAge = 0,
        maxAge = 99,
        publicationStatus,
        published = false,
        publicationEndedAt = null,
        publicationStartedAt = null,
        items: dtoItems = []
    } = dto;

    // Record for front-end
    return {
        shortId: id,
        id: uuid,
        title,
        description,
        createdAt: createdAt as string,
        status: status as ContentStatus,
        publicationStatus: publicationStatus as PublicationStatus,
        published,
        publicationEndedAt: publicationEndedAt as string,
        publicationStartedAt: publicationStartedAt as string,
        minAge,
        maxAge,
        items: dtoItems?.map((dtoItem) => {
            return {
                targetType: dtoItem.targetType as SelectionTargetType,
                targetId: dtoItem.targetUuid as string
            };
        })
    };
}

/**
 * Parses the record from frontend to DTO for backend
 * @param record
 * @returns SelectionDTO
 */

function mapRecordToDTO(record: SelectionRecord): SelectionDTO {
    // Record from front-end
    const {
        shortId = 0,
        id = "",
        title = "",
        description = "",
        status = "draft",
        publicationStatus,
        published = false,
        publicationStartedAt = null,
        publicationEndedAt = null,
        minAge = 0,
        maxAge = 99,
        items: recordItems = []
    } = record;

    // DTO for back-end
    return {
        id: shortId,
        title,
        uuid: id as string,
        description,
        status,
        publicationStatus,
        published,
        publicationStartedAt: publicationStartedAt as string,
        publicationEndedAt: publicationEndedAt as string,
        minAge,
        maxAge,
        items: recordItems?.map((recordItem) => {
            return {
                targetType: recordItem.targetType as string,
                targetUuid: recordItem.targetId
            };
        })
    };
}

interface SelectionFilters extends BaseFilters {
    q?: string;
    id: string;
    status: ContentStatus;
    publicationStatus: PublicationStatus;
}

async function getList(params: Readonly<GetListParams>): Promise<GetListResult<SelectionRecord>> {
    const { q: searchTerms, ...filters } = params.filter as SelectionFilters;

    let parameters: FetchArgType<typeof findAll> = {
        filter: convertIdFilter(filters),
        pageable: {
            page: params.pagination.page - 1,
            size: params.pagination.perPage,
            sort: isEmpty(params.sort.field) ? [] : [`${params.sort.field},${params.sort.order}`]
        }
    } as const;

    if (isDefined(searchTerms)) {
        parameters = {
            ...parameters,
            filter: { ...parameters.filter, searchTerms }
        };
    }

    const { data } = await findAll(parameters);
    const dataPage = data.content;
    assertIsDefined(dataPage);
    const total = dataPage.totalElements;
    assertIsDefined(total);
    assertIsDefined(dataPage.content);

    return {
        data: dataPage.content.map((content) => mapDTOToRecord({ id: content.id ?? 0, ...content })),
        total
    };
}

async function _getOne(params: LangParams | (number | string)): Promise<SelectionRecord> {
    const {
        data: { content: selectionDTO }
    } = await getOne({
        uuid: params as string
    });
    assertIsDefined(selectionDTO);

    return mapDTOToRecord({ id: selectionDTO.id ?? 0, ...selectionDTO });
}

async function getMany(uuids: readonly (number | string)[]): Promise<GetManyResult<SelectionRecord>> {
    const {
        data: { content: selectionDTO }
    } = await getAllByUuids({
        uuids: uuids as string[]
    });
    assertIsDefined(selectionDTO);

    return { data: await Promise.all(selectionDTO.map((dto) => mapDTOToRecord({ id: dto.id ?? 0, ...dto }))) };
}

async function getManyReference(_params: Readonly<GetManyReferenceParams>): Promise<GetManyReferenceResult<SelectionRecord>> {
    return Promise.reject(new NotImplementedError("getManyReference"));
}

async function update(params: Readonly<UpdateParams<SelectionRecord>>): Promise<UpdateResult<SelectionRecord>> {
    const payload = mapRecordToDTO(params.data as SelectionRecord);

    const parameters = { uuid: payload.uuid ?? "", ...payload };

    const { data } = await updateOne(parameters);
    const selectionDTO = data.content;
    assertIsDefined(selectionDTO);

    return { data: mapDTOToRecord({ id: parameters.id, ...selectionDTO }) };
}

async function updateMany(_params: Readonly<UpdateManyParams>): Promise<string[]> {
    return Promise.reject(new NotImplementedError("updateMany"));
}

async function create(params: Readonly<CreateParams>): Promise<CreateResult<SelectionRecord>> {
    const payload = mapRecordToDTO(params.data as any);

    assertIsDefined(payload.title);
    const parameters = { ...payload };
    delete (parameters as any)["id"];
    delete (parameters as any)["uuid"];
    const { data } = await createOne(parameters);
    const selectionDTO = data.content;

    assertIsDefined(selectionDTO);

    return { data: mapDTOToRecord({ id: selectionDTO.id as number, ...selectionDTO }) };
}

// eslint-disable-next-line no-underscore-dangle
async function _delete(uuid: number | string): Promise<SelectionRecord> {
    const {
        data: { content: isDeleted }
    } = await deleteOne({
        uuid: uuid as string
    });
    assertIsDefined(isDeleted);

    return {} as SelectionRecord;
}

async function deleteMany(params: Readonly<DeleteManyParams>): Promise<DeleteManyResult> {
    await Promise.all(params.ids.map((id: string) => _delete(id)));
    return {
        data: params.ids.map((id: string) => {
            id;
        })
    };
}

function getErrorDetails(_error: unknown, _operation: MpmDataProviderOperationName<SelectionRecord>): ErrorDetails | undefined {
    // eslint-disable-next-line no-undefined
    return undefined;
}

export const SelectionDataProvider: MpmDataProvider<SelectionRecord> = {
    create,
    delete: _delete,
    deleteMany,
    getErrorDetails,
    getList,
    getMany,
    getManyReference,
    getOne: _getOne,
    update,
    updateMany
};
